import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Subject, takeUntil } from 'rxjs';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { BrowserDetectorService } from './services/browser-detector.service';
import { SecurityService } from './services/security.service';
import { PERMISSIONS_DICTIONARY } from './utils/permissions-dictionary';

@Component({
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    ButtonModule,
    TieredMenuModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    NgOptimizedImage,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private primeNGConfig: PrimeNGConfig,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private browserDetectorService: BrowserDetectorService,
    private securityService: SecurityService
  ) {}

  isMobile = false;
  isIframe = false;
  private readonly _destroying$ = new Subject<void>();

  items: MenuItem[] | undefined;
  menuExpanded = true;

  toggleMenu(): void {
    this.menuExpanded = !this.menuExpanded;
  }

  async ngOnInit() {
    this.isMobile = this.browserDetectorService.isMobile();
    this.primeNGConfig.ripple = true;

    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }

    this.defineUserPermissions(this.authService.instance.getActiveAccount());
  }

  defineUserPermissions(account: AccountInfo | null): void {
    if (account) {
      this.securityService.getSecurityProfile().then(() => {
        const itemsTemplate = [
          {
            label: 'Home',
            icon: 'pi pi-home',
            route: 'home',
            enabled: true,
          },
          {
            label: 'My profile',
            icon: 'pi pi-user',
            route: 'profile',
            enabled: true,
          },
          {
            separator: true,
            enabled: true,
          },
          {
            label: 'Applications',
            icon: 'pi pi-desktop',
            route: 'applications',
            enabled: this.securityService.hasEntityAssigned(
              PERMISSIONS_DICTIONARY.applications.name
            ),
          },
          {
            label: 'Roles',
            icon: 'pi pi-users',
            route: 'roles',
            enabled: this.securityService.hasEntityAssigned(
              PERMISSIONS_DICTIONARY.roles.name
            ),
          },
          {
            label: 'Entities',
            icon: 'pi pi-folder',
            route: 'entities',
            enabled: this.securityService.hasEntityAssigned(
              PERMISSIONS_DICTIONARY.entities.name
            ),
          },
          {
            label: 'Permissions',
            icon: 'pi pi-check-square',
            route: 'permissions',
            enabled: this.securityService.hasEntityAssigned(
              PERMISSIONS_DICTIONARY.permissions.name
            ),
          },
        ];
        this.items = itemsTemplate.filter((item) => item.enabled);
      });
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
